import axios from "axios";

import { AuthRequest, AuthResponse, AccessTokenDecoded } from "@/types/auth";
import { jwtDecode } from "jwt-decode";
import cachedService from "@/services/cacheService";

const client_id = process.env.VUE_APP_CLIENT_ID as string;
const client_secret = process.env.VUE_APP_CLIENT_SECRET as string;
const auth_url = process.env.VUE_APP_AUTH_URL as string;
const admin_access_roles: string[] = process.env.VUE_APP_ADMIN_ACCESS_ROLES?.split(',') || [] as string[];
const manager_access_roles: string[] = process.env.VUE_APP_MANAGER_ACCESS_ROLES?.split(',') || [] as string[];
const kindergarten_head_access_roles: string[] = process.env.VUE_APP_KINDERGARTEN_HEAD_ACCESS_ROLES?.split(',') || [] as string[];
const accountant_access_roles: string[] = process.env.VUE_APP_ACCOUNTANT_ACCESS_ROLES?.split(',') || [] as string[];
const manager_spk_access_roles: string[] = process.env.VUE_APP_SPK_ACCESS_ROLES?.split(',') || [] as string[];
const findir_roles: string[] = process.env.VUE_APP_FIN_DIR_ROLES?.split(',') || [] as string[];
const access_roles : string[] = [...admin_access_roles, ...manager_access_roles, ...kindergarten_head_access_roles, ...accountant_access_roles, ...manager_spk_access_roles, ...findir_roles];

const api = axios.create({
  baseURL: auth_url,
  headers: { "Content-Type": "application/x-www-form-urlencoded" },
});

api.interceptors.response.use((response) => response,
    async (error) => {
      if (error.response && error.response.status === 400) {
        const data = error.response.data;
        if (data['error'] == "invalid_grant" && data['error_description'] == "Account is not fully set up") {
          window.location.href = "/reset";
        }
      }
    });

async function login(requestData: AuthRequest): Promise<AuthResponse> {
  const params = new URLSearchParams();
  params.append("username", requestData.username);
  params.append("password", requestData.password);
  params.append("client_id", client_id);
  params.append("client_secret", client_secret);
  params.append("grant_type", "password");

  const response = await api.post<AuthResponse>(auth_url, params);
  if (response.data.access_token && checkRolesFromToken(response.data.access_token)) {
    saveTokens(response.data, requestData.username);
    if (hasAccess([...getManagerRoles(), ...getAdminRoles()])) {
      Promise.resolve().then(() => {
        cachedService.useKatoStore().refreshKato();
        cachedService.useOwnershipType().refreshOwnershipType();
        cachedService.useDurationTime().refreshDurationTime();
        cachedService.useRegionGroup().refreshRegionGroup();
        cachedService.useRegionGroup().refreshRegionGroup();
        cachedService.useLocalityType().refreshLocalityType();
        cachedService.useEducationalType().refreshEducationalType();
        cachedService.useRegionType().refreshRegionType();
      });
    }
    return response.data;
  }
  return {
    access_token: null,
    refresh_token: "",
    username: ""
  };
}

function checkRoleAccessFromToken(access_token: string, roles: string[]): boolean {
  if (!access_token) return false;
  const decoded: AccessTokenDecoded = jwtDecode(access_token);
  return roles.some(role => decoded.realm_access.roles.includes(role));
}

function checkRolesFromToken(access_token: string): boolean {
  const decoded: AccessTokenDecoded = jwtDecode(access_token);
  return access_roles.some(role => decoded.realm_access.roles.includes(role));
}

async function refreshToken(): Promise<AuthResponse> {
  const refresh_token = localStorage.getItem("refresh_token");
  const username = localStorage.getItem("username");
  const params = new URLSearchParams();
  params.append("refresh_token", refresh_token || "");
  params.append("client_id", client_id);
  params.append("client_secret", client_secret);
  params.append("grant_type", "refresh_token");
  params.append("username", username || "");

  try {
    const response = await api.post<AuthResponse>(auth_url, params);
    if (response.data.access_token) {
      saveTokens(response.data, username || "");
    }
    return response.data;
  } catch (error) {
    console.error("Failed to refresh token", error);
    throw error;
  }
}

function saveTokens(data: AuthResponse, username: string): void {
  localStorage.setItem("access_token", data.access_token || "");
  localStorage.setItem("refresh_token", data.refresh_token);
  localStorage.setItem("username", username);
}

function logout(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("username");
}

function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

function getAdminRoles(): string[] {
  return admin_access_roles || [];
}

/** Администраторы с правом изменять данные — удаление, восстановление, просмотр удалённых. */
function getAdminWriteRoles(): string[] {
  return ["ROLE_DO_ADMIN"];
}

function getManagerRoles(): string[] {
  return manager_access_roles || [];
}

function getKindergartenHeadRoles(): string[] {
  return kindergarten_head_access_roles || [];
}

function getAccountantRoles(): string[] {
  return accountant_access_roles || [];
}
function getManagerSpkRoles(): string[] {
  return manager_spk_access_roles || [];
}
function getFinDirRoles(): string[] {
  return findir_roles || [];
}

function hasAccess(requiredRoles: string[]) {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  return checkRoleAccessFromToken(token, requiredRoles);
}

export default {
  login,
  refreshToken,
  getAccessToken,
  logout,
  checkRoleAccessFromToken,
  getManagerRoles,
  getKindergartenHeadRoles,
  getAccountantRoles,
  getAdminRoles,
  getAdminWriteRoles,
  getManagerSpkRoles,
  getFinDirRoles,
  hasAccess
};
